import React, { useEffect, useState, useMemo } from 'react';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { C } from '../../theme';
import CatalogueTable, { fieldKeyToOverrideKey, mapOverrideToFields, normalizeGlobalPlatform } from './CatalogueTable';

// Client page's "Catalogue" tab (catalogue-parity step 2, 2026-10-04) — the shared CatalogueTable
// with scope='client'. Resolution order is unchanged from the existing 3-layer merge used
// everywhere else: client value wins over global, and a plan's own frozen snapshot (not loaded
// here) always wins over both — this tab only ever touches the client-override layer.
export default function ClientCatalogueTab({ clientId }) {
  const { canEditCatalogue, hasSites, isSuperAdmin } = useAuth();
  const [globalPlatforms, setGlobalPlatforms] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([api.getPlatforms(), api.getClientCatalogueOverrides(clientId), api.getSites()])
      .then(([p, ovr, s]) => { setGlobalPlatforms(p); setOverrides(ovr||{}); setSites(s); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // normalizeGlobalPlatform applies the exact same per-field `||`/`??` fallbacks as the engine's
  // own catalogue merge (DigitalPlanningEngine.js) — without it, a legitimate falsy value (e.g.
  // vtr=0) would display/compare differently here than it does once a plan is actually built from
  // it (found 2026-10-04 while building step 3a's live-value comparison; fixed here too for the
  // same reason: this tab's values must match what downstream actually sees).
  const globalByDbId = useMemo(()=>Object.fromEntries(globalPlatforms.map(p=>[p.id, normalizeGlobalPlatform(p)])), [globalPlatforms]);

  // Layer 1 (global) + Layer 2 (client override) merge — same shape/precedence as the engine's own
  // catalogue merge (DigitalPlanningEngine.js), just without a Layer 3 plan snapshot here (this tab
  // edits the client layer only; a saved plan's own frozen values are untouched by anything below).
  const effectivePlatforms = useMemo(()=>globalPlatforms.map(p => {
    const ovr = overrides[String(p.id)] || {};
    return { ...p, ...normalizeGlobalPlatform(p), id:'db_'+p.id, _db_id:p.id, ...mapOverrideToFields(ovr), _client_overrides: ovr };
  }), [globalPlatforms, overrides]);

  async function persist({ isNew, id, data }) {
    if (isNew) return; // client scope never creates new platforms
    await api.updateClientCatalogueOverrides(clientId, { [String(id)]: data });
  }
  async function onResetField(dbId, fieldKey) {
    await api.deleteClientCatalogueOverrideField(clientId, dbId, fieldKeyToOverrideKey(fieldKey));
    load();
  }
  async function onResetAll() {
    await api.updateClient(clientId, { catalogue_overrides: {} });
    load();
  }

  if (loading) return <div style={C.empty}>Loading…</div>;
  return (
    <div>
      <div style={{ fontSize:12, color:'#8aa0b8', marginBottom:16 }}>
        CPM, CPC, discounts, performance metrics, frequency, audience comp. and volume caps can be overridden for this client. Identity fields (type, format group, reach pool{(hasSites||isSuperAdmin)?', site, device, overlap group':''}) always come from the global catalogue.
      </div>
      <CatalogueTable
        scope="client"
        platforms={effectivePlatforms}
        sites={sites}
        canEdit={canEditCatalogue}
        persist={persist}
        onReload={load}
        showPublisherCols={hasSites||isSuperAdmin}
        globalByDbId={globalByDbId}
        onResetField={onResetField}
        onResetAll={onResetAll}
      />
    </div>
  );
}
